import { Link } from 'react-router-dom';

export default function Terms() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-lg mb-4 border-2 border-red-200">
            <span className="text-3xl">🍁</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Terms and Conditions
          </h1>
          <p className="text-gray-600">Last Updated: January 9, 2026</p>
        </div>

        {/* Content */}
        <div className="prose prose-gray max-w-none space-y-6 text-gray-700">
          {/* Agreement */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">1. Agreement to Terms</h2>
            <p>
              By accessing and using this Canadian Citizenship Test practice platform ("Service"), you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you may not use our Service.
            </p>
          </section>

          {/* Educational Purpose */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">2. Educational Purpose Only</h2>
            <p>
              This Service is provided for <strong>educational and practice purposes only</strong>. It is designed to help you prepare for the Canadian Citizenship Test but does not guarantee success on the official examination.
            </p>
            <p className="mt-2">
              All questions and content are based on the official <em>"Discover Canada: The Rights and Responsibilities of Citizenship"</em> study guide published by Immigration, Refugees and Citizenship Canada (IRCC).
            </p>
          </section>

          {/* No Liability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">3. No Liability and Disclaimer</h2>
            <p className="font-semibold text-red-600">
              IMPORTANT: We assume NO LIABILITY for your performance on the official Canadian Citizenship Test.
            </p>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-4">
              <p className="font-semibold text-yellow-800">You acknowledge and agree that:</p>
              <ul className="mt-2 space-y-2 text-yellow-700">
                <li>• This is a <strong>practice tool</strong>, not an official government service</li>
                <li>• We are <strong>not affiliated</strong> with Immigration, Refugees and Citizenship Canada (IRCC)</li>
                <li>• Success in our practice tests <strong>does not guarantee</strong> success on the official test</li>
                <li>• You are <strong>solely responsible</strong> for your own test preparation and results</li>
                <li>• We provide <strong>no warranties</strong> regarding the accuracy, completeness, or timeliness of content</li>
              </ul>
            </div>
            <p className="mt-3">
              <strong>We strongly recommend</strong> that you also:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Study the official "Discover Canada" guide thoroughly</li>
              <li>Use multiple study resources and methods</li>
              <li>Attend citizenship classes if available in your area</li>
              <li>Consult official IRCC resources at <a href="https://www.canada.ca/en/immigration-refugees-citizenship.html" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">canada.ca</a></li>
            </ul>
          </section>

          {/* User Accounts */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">4. User Accounts</h2>
            <p>
              When you create an account, you agree to:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Provide accurate and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>
            <p className="mt-3">
              You may not share your account with others or use another person's account without permission.
            </p>
          </section>

          {/* Payment Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">5. Payment and Refunds</h2>
            <p>
              <strong>Premium Membership:</strong> A one-time payment of $14.99 CAD grants lifetime access to all premium features.
            </p>
            <p className="mt-2">
              <strong>Refund Policy:</strong> All sales are final. We do not offer refunds except as required by law. If you experience technical issues, please contact our support team.
            </p>
            <p className="mt-2">
              <strong>Payment Processing:</strong> Payments are processed securely through Stripe. We do not store your credit card information.
            </p>
          </section>

          {/* Prohibited Uses */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">6. Prohibited Uses</h2>
            <p>You may not use this Service to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Violate any laws or regulations</li>
              <li>Share or distribute copyrighted content without permission</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Use automated tools (bots, scrapers) to access the Service</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Upload viruses, malware, or malicious code</li>
              <li>Impersonate any person or entity</li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">7. Intellectual Property</h2>
            <p>
              The Service and its original content (excluding questions from the official guide), features, and functionality are owned by us and are protected by international copyright, trademark, and other intellectual property laws.
            </p>
            <p className="mt-2">
              Questions are based on the official "Discover Canada" guide, which is public domain content provided by the Government of Canada.
            </p>
          </section>

          {/* Data and Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">8. Data Collection and Privacy</h2>
            <p>
              We collect and store:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Account information (username, email, password hash)</li>
              <li>Quiz performance and progress data</li>
              <li>Payment information (processed by Stripe, not stored by us)</li>
            </ul>
            <p className="mt-3">
              We will never sell your personal information to third parties. Your data is stored securely and used solely to provide and improve our Service.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">9. Termination</h2>
            <p>
              We reserve the right to terminate or suspend your account immediately, without prior notice, if you breach these Terms and Conditions.
            </p>
            <p className="mt-2">
              Upon termination, your right to use the Service will cease immediately, but payment obligations remain (no refunds for premium memberships).
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">10. Limitation of Liability</h2>
            <p className="font-semibold">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </p>
            <div className="bg-gray-50 border border-gray-300 p-4 mt-3 rounded-lg">
              <p className="mb-2">
                We shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Loss of profits or revenue</li>
                <li>Loss of data or information</li>
                <li>Failure to pass the official citizenship test</li>
                <li>Immigration consequences</li>
                <li>Emotional distress</li>
              </ul>
              <p className="mt-3 font-semibold">
                Our total liability shall not exceed the amount you paid for the Service ($14.99 CAD).
              </p>
            </div>
          </section>

          {/* Disclaimer of Warranties */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">11. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>
            <p className="mt-3">
              We do not warrant that:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>The Service will be uninterrupted or error-free</li>
              <li>All questions are 100% accurate or up-to-date</li>
              <li>Using the Service will result in passing the official test</li>
              <li>The Service will meet your specific requirements</li>
            </ul>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">12. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of any material changes by updating the "Last Updated" date at the top of this page.
            </p>
            <p className="mt-2">
              Your continued use of the Service after changes constitutes acceptance of the updated Terms.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">13. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of Canada and the province where our business is registered, without regard to conflict of law provisions.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">14. Contact Us</h2>
            <p>
              If you have any questions about these Terms and Conditions, please contact us at:
            </p>
            <p className="mt-2 font-semibold">
              Email: support@canadacitizenshiptest.com (update with actual email)
            </p>
          </section>

          {/* Acknowledgment */}
          <section className="bg-red-50 border-l-4 border-red-500 p-6 mt-8">
            <h2 className="text-xl font-bold text-red-900 mb-3">Acknowledgment</h2>
            <p className="text-red-800">
              By using this Service, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. You understand that this is a practice tool only and that you are solely responsible for your citizenship test preparation and results.
            </p>
          </section>
        </div>

        {/* Back Button */}
        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
